import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Input from '../components/elements/Input';
import { useAuth } from '../contexts/AuthContext';
import { createInvoice, getInvoiceById, updateInvoice } from '../services/invoices';
import { getFileOpeningById } from '../services/fileOpenings';
import toast from 'react-hot-toast';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import InvoicePdf from '../components/pdf/InvoicePdf';
import { renderAddress } from '../utils/helper';

const InvoiceCreate = () => {
    const { id, invoiceId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        client_name: '',
        address: '',
        phone: '',
        mobile: '',
        comments: '',
        sub_total: '0', // Store as string for input compatibility
        vat_percentage: '0',
        vat_total: '0',
        grand_total: '0',
        status: 'draft'
    });
    const [loading, setLoading] = useState(true);
    const [showPdfPreview, setShowPdfPreview] = useState(false);

    // Calculate totals only when sub_total or vat_percentage changes
    useEffect(() => {
        const subTotalNum = parseFloat(formData.sub_total) || 0;
        const vatPercentage = parseFloat(formData.vat_percentage) || 0;
        const vatTotal = subTotalNum * (vatPercentage / 100);
        const grandTotal = subTotalNum + vatTotal;

        setFormData(prevState => ({
            ...prevState,
            vat_total: vatTotal.toFixed(2),
            grand_total: grandTotal.toFixed(2)
        }));
    }, [formData.sub_total, formData.vat_percentage]);

    // Fetch data in proper sequence
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Always fetch file opening data first
                const fileData = await getFileOpeningById(id);

                let initialFormData = {
                    date: new Date().toISOString().split('T')[0],
                    client_name: fileData.title + " " + fileData.forename + " " + fileData.surname,
                    address: `${renderAddress(fileData)}`,
                    phone: fileData.telephone || '',
                    mobile: fileData.mobile || '',
                    comments: '',
                    sub_total: fileData.sub_total?.toString() || '0',
                    vat_percentage: '0',
                    vat_total: '0',
                    grand_total: '0',
                    status: 'draft'
                };

                // If editing existing invoice, fetch and merge invoice data
                if (invoiceId) {
                    const invoiceData = await getInvoiceById(invoiceId);
                    initialFormData = {
                        ...initialFormData,
                        ...invoiceData,
                        date: invoiceData.date ? invoiceData.date.split('T')[0] : initialFormData.date,
                        sub_total: invoiceData.sub_total?.toString() || initialFormData.sub_total,
                        vat_percentage: invoiceData.vat_percentage?.toString() || initialFormData.vat_percentage,
                        vat_total: invoiceData.vat_total?.toString() || '0',
                        grand_total: invoiceData.grand_total?.toString() || '0'
                    };
                }

                setFormData(initialFormData);

            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, invoiceId]); // Depend on both id and invoiceId

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const getDataToSubmit = () => {
        return {
            ...formData,
            fid: id,
            user_id: user?.id,
            sub_total: parseFloat(formData.sub_total) || 0,
            vat_percentage: parseFloat(formData.vat_percentage) || 0,
            vat_total: parseFloat(formData.vat_total) || 0,
            grand_total: parseFloat(formData.grand_total) || 0,
            date: formData.date || new Date().toISOString().split('T')[0]
        };
    };

    const downloadPdf = async (invoiceData) => {
        try {
            const blob = await pdf(<InvoicePdf data={invoiceData} />).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice-${invoiceData.id || 'new'}-${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Failed to generate PDF');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const dataToSubmit = getDataToSubmit();
        try {
            if (invoiceId) {
                await updateInvoice(invoiceId, dataToSubmit);
                toast.success('Invoice updated successfully!');
                await downloadPdf(dataToSubmit);
                navigate(`/file-opening/${id}/invoice/${invoiceId}`);
            } else {
                const data = await createInvoice(dataToSubmit);
                toast.success('Invoice created successfully!');
                await downloadPdf(data);
                //navigate(`/file-opening/${id}/invoices`);
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    if (loading) {
        return <DashboardLayout pageTitle="Loading..."><p className='dark:text-white'>Loading...</p></DashboardLayout>;
    }

    return (
        <DashboardLayout pageTitle={invoiceId ? 'Edit Invoice' : 'Create New Invoice'}>
            <div className="max-w-4xl mx-auto">
                <div className='mb-4'>

                    <div>
                        <div className='flex justify-between'>
                            <div>
                                <button
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow ml-4"
                                >
                                    Back
                                </button>
                            </div>
                            {showPdfPreview && (
                                <div className="text-right">
                                    <button
                                        type="button"
                                        onClick={() => setShowPdfPreview(!showPdfPreview)}
                                        className="mr-4 mb-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                                    >
                                        {!showPdfPreview ? 'Show Preview' : "Hide Preview"}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className='mb-4'>
                            {showPdfPreview && getDataToSubmit() && (
                                <div style={{ height: '80vh' }} className="mb-4 text-right">
                                    <PDFViewer width="100%" height="100%">
                                        <InvoicePdf data={getDataToSubmit()} />
                                    </PDFViewer>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={showPdfPreview ? "h-10" : ""}></div>
                <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 sm:p-8 mb-10">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 dark:text-white">
                            {invoiceId ? 'Edit Invoice' : 'Create New Invoice'}
                        </h1>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-4">
                            <Input
                                label="Date"
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                required
                            />

                            <Input
                                label="Client Name"
                                type="text"
                                name="client_name"
                                value={formData.client_name}
                                onChange={handleChange}
                                required
                            />

                            <div>
                                <label className="block font-semibold mb-1 text-gray-800 dark:text-white">
                                    Address
                                </label>
                                <textarea
                                    name="address"
                                    className="w-full border rounded-md p-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                                    rows="4"
                                    placeholder="Enter client address..."
                                    value={formData.address}
                                    onChange={handleChange}
                                ></textarea>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                            <Input
                                label="Phone"
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                            />

                            <Input
                                label="Mobile"
                                type="tel"
                                name="mobile"
                                value={formData.mobile}
                                onChange={handleChange}
                            />

                            <div>
                                <label className="block font-semibold mb-1 text-gray-800 dark:text-white">
                                    Comments
                                </label>
                                <textarea
                                    name="comments"
                                    className="w-full border rounded-md p-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                                    rows="4"
                                    placeholder="Enter any comments..."
                                    value={formData.comments}
                                    onChange={handleChange}
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Totals Section */}
                    <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Invoice Totals</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Input
                                label="Sub Total (£)"
                                type="number"
                                name="sub_total"
                                value={formData.sub_total}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                required
                            />

                            <Input
                                label="VAT Percentage (%)"
                                type="number"
                                name="vat_percentage"
                                value={formData.vat_percentage}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                max="100"
                            />

                            <Input
                                label="VAT Total (£)"
                                type="number"
                                name="vat_total"
                                value={formData.vat_total}
                                readOnly
                                className="bg-gray-100 dark:bg-gray-600"
                            />

                            <Input
                                label="Grand Total (£)"
                                type="number"
                                name="grand_total"
                                value={formData.grand_total}
                                readOnly
                                className="bg-gray-100 dark:bg-gray-600 font-semibold"
                            />
                        </div>
                    </div>

                    {/* Status Field */}
                    <div className="mt-6">
                        <label className="block font-semibold mb-1 text-gray-800 dark:text-white">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full border rounded-md p-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        >
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                            <option value="paid">Paid</option>
                            <option value="overdue">Overdue</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    <div className="flex justify-end mt-8">
                        <button
                            type="button"
                            onClick={() => setShowPdfPreview(!showPdfPreview)}
                            className="mr-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                        >
                            {!showPdfPreview ? 'Show Preview' : "Hide Preview"}
                        </button>
                        <button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                        >
                            {invoiceId ? 'Update Invoice' : 'Create Invoice'}
                        </button>
                    </div>
                </form>
            </div>
        </DashboardLayout>
    );
};

export default InvoiceCreate;